import { useRef, useState } from 'react';

/** 도장 그림의 긴 변 — 서명란에서 13mm 로 찍히므로 이만하면 넉넉하다. 서버 상한(1MB) 안에 든다. */
const MAX_SIDE = 480;

/**
 * 도장 올리기 (V633 · 사용자 지시 2026-11-04 「서명이나 도장을 찍을 수 있게 해 협력업체에서도」).
 *
 * <p>사진·스캔한 도장 그림을 받아 <b>흰 바탕을 투명하게</b> 바꾸고 줄여 PNG data URL 로 낸다 — 흰 네모가 글자를 가리지
 * 않고 종이에 찍힌 도장처럼 겹치게 하려는 것이다. 그림은 이 기기 안에서만 다룬다.
 */
export function StampPicker({ value, onChange }: { value: string | null; onChange: (value: string | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      onChange(await toTransparentPng(file));
    } catch {
      setError('그림을 읽지 못했습니다. PNG · JPG 파일을 골라 주세요.');
      onChange(null);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className="flex h-28 w-28 shrink-0 items-center justify-center rounded-lg"
        style={{ border: '1px dashed var(--border-strong, #94a3b8)', background: '#fff' }}
      >
        {value ? <img src={value} alt="도장" className="max-h-24 max-w-24" /> : null}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(event) => {
            void pick(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <button type="button" className="btn-secondary" onClick={() => input.current?.click()}>
          {value ? '다른 도장' : '도장 올리기'}
        </button>
        {error && (
          <span className="text-ui-xs" style={{ color: 'var(--danger, #dc2626)' }}>
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

async function toTransparentPng(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = image.data;
  // 밝은 바탕(흰 종이)을 지운다 — 밝을수록 더 투명하게. 도장의 붉은 잉크는 남는다.
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i] ?? 0;
    const g = px[i + 1] ?? 0;
    const b = px[i + 2] ?? 0;
    const light = Math.min(r, g, b);
    if (light > 235) px[i + 3] = 0;
    else if (light > 200) px[i + 3] = Math.round(((235 - light) / 35) * (px[i + 3] ?? 255));
  }
  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL('image/png');
}
